import { useState } from "react";
import { ImagePreviewOverlay } from "../components/ui/ImagePreviewOverlay";
import { SurveyHostThemeProvider } from "../theme/SurveyHostThemeProvider";
import { ChatSurveyHost } from "./hosts/chat/ChatSurveyHost";
import { FormsSurveyHost } from "./hosts/forms/FormsSurveyHost";
import { HostOverlayProvider } from "./hosts/shared/HostOverlay";
import type { SurveyHostProps } from "./hosts/shared/HostShared";
import type { QuestionHost } from "./hosts/shared/types";
import type { AttemptDraftState } from "./sessionSlot";
import type { HostCapabilities } from "./contracts";

const capabilities: HostCapabilities = {
  controls: ["primary-action", "text-input"],
  inputModes: ["single-line", "multiline"],
};

const surveyHosts = {
  chat: { component: ChatSurveyHost, capabilities },
  forms: { component: FormsSurveyHost, capabilities },
} satisfies Record<
  QuestionHost,
  {
    component: React.ComponentType<SurveyHostProps>;
    capabilities: HostCapabilities;
  }
>;

export const SessionSurveyHost = ({
  host,
  draftState: sharedDraftState,
  setDraftState: setSharedDraftState,
  ...props
}: Omit<SurveyHostProps, "capabilities"> & { host: QuestionHost }) => {
  const [localDraftState, setLocalDraftState] =
    useState<AttemptDraftState | null>(null);
  const draftState =
    sharedDraftState === undefined ? localDraftState : sharedDraftState;
  const setDraftState = setSharedDraftState ?? setLocalDraftState;
  const registration = surveyHosts[host];
  const Host = registration.component;
  return (
    <SurveyHostThemeProvider host={host}>
      <HostOverlayProvider host={host}>
        <Host
          {...props}
          capabilities={registration.capabilities}
          draftState={draftState}
          setDraftState={setDraftState}
        />
        <ImagePreviewOverlay />
      </HostOverlayProvider>
    </SurveyHostThemeProvider>
  );
};
